import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { PDFControls } from './PDFControls';
import { Loader2, FileX } from 'lucide-react';

import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface PDFViewerProps {
  pdfData: Uint8Array | null;
  loading?: boolean;
  error?: string | null;
  onDownload?: () => void;
  onHistoryClick?: () => void;
  onCommentsClick?: () => void;
  historyActive?: boolean;
  commentsActive?: boolean;
  commentCount?: number;
}

export function PDFViewer({
  pdfData,
  loading,
  error,
  onDownload,
  onHistoryClick,
  onCommentsClick,
  historyActive = false,
  commentsActive = false,
  commentCount = 0,
}: PDFViewerProps) {
  const [numPages, setNumPages] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [scale, setScale] = useState<number>(1);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const [restoringView, setRestoringView] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const pageRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const renderedPagesRef = useRef<Set<number>>(new Set());
  const pendingScrollRestoreRef = useRef<{
    pageNumber: number;
    pageOffsetRatio: number;
    fallbackRatio: number;
    sourcePdfUrl: string | null;
  } | null>(null);
  const previousLoadingRef = useRef(loading);
  const restoreAnimationFrameRef = useRef<number | null>(null);
  const restoreAttemptRef = useRef(0);
  const restoreStableFrameCountRef = useRef(0);
  const restoreScrollHeightRef = useRef(0);

  const finishRestore = useCallback(() => {
    pendingScrollRestoreRef.current = null;
    restoreAttemptRef.current = 0;
    restoreStableFrameCountRef.current = 0;
    restoreScrollHeightRef.current = 0;
    setRestoringView(false);
  }, []);

  const captureViewPosition = useCallback(() => {
    const container = containerRef.current;
    if (!container || numPages === 0) return;

    const pageElement = pageRefs.current.get(currentPage);
    const maxScrollTop = Math.max(container.scrollHeight - container.clientHeight, 0);
    const fallbackRatio = maxScrollTop > 0 ? container.scrollTop / maxScrollTop : 0;

    if (!pageElement || pageElement.offsetHeight <= 0) {
      pendingScrollRestoreRef.current = {
        pageNumber: currentPage,
        pageOffsetRatio: 0,
        fallbackRatio,
        sourcePdfUrl: pdfUrl,
      };
      return;
    }

    pendingScrollRestoreRef.current = {
      pageNumber: currentPage,
      pageOffsetRatio: Math.max(
        0,
        Math.min(1, (container.scrollTop - pageElement.offsetTop) / pageElement.offsetHeight)
      ),
      fallbackRatio,
      sourcePdfUrl: pdfUrl,
    };
  }, [currentPage, numPages, pdfUrl]);

  const restoreViewPosition = useCallback(() => {
    const container = containerRef.current;
    const pendingRestore = pendingScrollRestoreRef.current;
    if (!container || !pendingRestore || numPages === 0) return false;

    const targetPage = Math.min(Math.max(1, pendingRestore.pageNumber), numPages);
    const targetPageElement = pageRefs.current.get(targetPage);
    const targetPageRendered = renderedPagesRef.current.has(targetPage);

    if (targetPageElement && targetPageElement.offsetHeight > 0) {
      container.scrollTop = Math.max(
        0,
        targetPageElement.offsetTop + (targetPageElement.offsetHeight * pendingRestore.pageOffsetRatio)
      );
      setCurrentPage(targetPage);
      return targetPageRendered;
    }

    const maxScrollTop = Math.max(container.scrollHeight - container.clientHeight, 0);
    if (targetPageRendered && maxScrollTop > 0) {
      container.scrollTop = pendingRestore.fallbackRatio * maxScrollTop;
      return true;
    }

    return false;
  }, [numPages]);

  const clearScheduledRestore = useCallback(() => {
    if (restoreAnimationFrameRef.current !== null) {
      window.cancelAnimationFrame(restoreAnimationFrameRef.current);
      restoreAnimationFrameRef.current = null;
    }
  }, []);

  const scheduleRestoreViewPosition = useCallback(() => {
    if (loading || !pendingScrollRestoreRef.current) return;

    const targetDocumentChanged = pendingScrollRestoreRef.current.sourcePdfUrl !== pdfUrl;
    if (!targetDocumentChanged) return;

    clearScheduledRestore();
    restoreAttemptRef.current = 0;
    restoreStableFrameCountRef.current = 0;
    restoreScrollHeightRef.current = containerRef.current?.scrollHeight ?? 0;

    const attemptRestore = () => {
      restoreAnimationFrameRef.current = null;

      if (loading || !pendingScrollRestoreRef.current) {
        return;
      }

      const restored = restoreViewPosition();
      const container = containerRef.current;

      if (restored && container) {
        if (container.scrollHeight === restoreScrollHeightRef.current) {
          restoreStableFrameCountRef.current += 1;
        } else {
          restoreScrollHeightRef.current = container.scrollHeight;
          restoreStableFrameCountRef.current = 0;
        }

        if (restoreStableFrameCountRef.current >= 2) {
          finishRestore();
          return;
        }
      } else if (container) {
        restoreScrollHeightRef.current = container.scrollHeight;
        restoreStableFrameCountRef.current = 0;
      }

      restoreAttemptRef.current += 1;
      if (restoreAttemptRef.current >= 180) {
        finishRestore();
        return;
      }

      restoreAnimationFrameRef.current = window.requestAnimationFrame(attemptRestore);
    };

    restoreAnimationFrameRef.current = window.requestAnimationFrame(attemptRestore);
  }, [clearScheduledRestore, finishRestore, loading, pdfUrl, restoreViewPosition]);

  useEffect(() => {
    if (pdfData) {
      const blob = new Blob([pdfData], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setPdfUrl(null);
    }
  }, [pdfData]);

  useEffect(() => {
    pageRefs.current.clear();
    renderedPagesRef.current.clear();
  }, [pdfUrl]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const updateWidth = () => {
      setContainerWidth(container.clientWidth);
    };

    updateWidth();

    const observer = new ResizeObserver(updateWidth);
    observer.observe(container);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!previousLoadingRef.current && loading) {
      captureViewPosition();
      setRestoringView(!!pendingScrollRestoreRef.current);
    } else if (!loading && !pendingScrollRestoreRef.current) {
      setRestoringView(false);
    }

    previousLoadingRef.current = loading;
  }, [captureViewPosition, loading]);

  useEffect(() => {
    return () => {
      clearScheduledRestore();
    };
  }, [clearScheduledRestore]);

  useEffect(() => {
    if (pdfUrl) return;
    finishRestore();
  }, [finishRestore, pdfUrl]);

  function onDocumentLoadSuccess({ numPages }: { numPages: number }) {
    setNumPages(numPages);
    if (pendingScrollRestoreRef.current) {
      setRestoringView(true);
      setCurrentPage(Math.min(Math.max(1, pendingScrollRestoreRef.current.pageNumber), numPages));
      scheduleRestoreViewPosition();
      return;
    }

    setCurrentPage(1);
  }

  const handlePageRenderSuccess = useCallback((pageNumber: number) => {
    renderedPagesRef.current.add(pageNumber);

    if (!pendingScrollRestoreRef.current || loading) {
      return;
    }

    scheduleRestoreViewPosition();
  }, [loading, scheduleRestoreViewPosition]);

  const scrollToPage = useCallback((pageNum: number) => {
    const pageElement = pageRefs.current.get(pageNum);
    if (pageElement) {
      pageElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  function goToPrevPage() {
    const newPage = Math.max(1, currentPage - 1);
    setCurrentPage(newPage);
    scrollToPage(newPage);
  }

  function goToNextPage() {
    const newPage = Math.min(numPages, currentPage + 1);
    setCurrentPage(newPage);
    scrollToPage(newPage);
  }

  const handleScroll = useCallback(() => {
    if (!containerRef.current || numPages === 0 || loading || restoringView) return;

    const container = containerRef.current;
    const containerTop = container.scrollTop;
    const containerHeight = container.clientHeight;
    const containerCenter = containerTop + containerHeight / 2;

    let closestPage = 1;
    let closestDistance = Infinity;

    pageRefs.current.forEach((element, pageNum) => {
      const pageTop = element.offsetTop;
      const pageHeight = element.offsetHeight;
      const pageCenter = pageTop + pageHeight / 2;
      const distance = Math.abs(containerCenter - pageCenter);

      if (distance < closestDistance) {
        closestDistance = distance;
        closestPage = pageNum;
      }
    });

    if (closestPage !== currentPage) {
      setCurrentPage(closestPage);
    }
  }, [currentPage, loading, numPages, restoringView]);

  function zoomIn() {
    setScale((prev) => Math.min(3, prev + 0.25));
  }

  function zoomOut() {
    setScale((prev) => Math.max(0.5, prev - 0.25));
  }

  function fitWidth() {
    setScale(1);
  }

  useEffect(() => {
    if (loading) {
      clearScheduledRestore();
      return;
    }

    if (!pendingScrollRestoreRef.current) {
      setRestoringView(false);
      return;
    }

    setRestoringView(true);

    if (!pdfUrl || numPages === 0) {
      return;
    }

    scheduleRestoreViewPosition();
  }, [clearScheduledRestore, loading, numPages, pdfUrl, scheduleRestoreViewPosition]);

  const pageWidth = containerWidth > 0
    ? Math.max(200, Math.floor((containerWidth - 8) * scale))
    : undefined;
  const [title, message, suggestion] = error ? error.split('\n') : [];
  const showErrorState = !loading && !!error;
  const showEmptyState = !loading && !error && !pdfUrl;
  const showDocument = !!pdfUrl;
  const showLoadingOverlay = (loading || restoringView) && showDocument;
  const showLoadingOnly = !!loading && !showDocument;
  const loadingMessage = loading ? 'Compiling...' : 'Restoring view...';

  return (
    <div className="h-full flex flex-col bg-editor-panel">
      <PDFControls
        pageNumber={currentPage}
        numPages={numPages}
        scale={scale}
        onPrevPage={goToPrevPage}
        onNextPage={goToNextPage}
        onZoomIn={zoomIn}
        onZoomOut={zoomOut}
        onFitWidth={fitWidth}
        onDownload={onDownload}
        onHistoryClick={onHistoryClick}
        onCommentsClick={onCommentsClick}
        canDownload={!!pdfData && pdfData.byteLength > 0}
        historyActive={historyActive}
        commentsActive={commentsActive}
        commentCount={commentCount}
      />
      <div
        ref={containerRef}
        data-testid="pdf-scroll-container"
        className="relative flex-1 overflow-auto p-4"
        onScroll={handleScroll}
      >
        {showLoadingOnly ? (
          <div className="h-full flex flex-col items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600 mb-2" />
            <p className="text-gray-600 text-sm">Compiling...</p>
          </div>
        ) : showErrorState ? (
          <div className="h-full flex flex-col items-center justify-center p-4">
            <FileX className="h-12 w-12 text-red-400 mb-3" />
            <p className="text-red-600 font-medium mb-1">{title || 'Compilation Error'}</p>
            {message && <p className="text-gray-700 text-sm text-center max-w-md mb-3">{message}</p>}
            {suggestion && (
              <p className="text-blue-700 bg-blue-50 rounded-lg px-4 py-2 text-sm max-w-md text-center">
                Tip: {suggestion}
              </p>
            )}
          </div>
        ) : showEmptyState ? (
          <div className="h-full flex flex-col items-center justify-center p-4">
            <FileX className="h-12 w-12 text-gray-300 mb-2" />
            <p className="text-gray-500 text-sm text-center">
              Click "Compile" to generate PDF preview
            </p>
          </div>
        ) : showDocument ? (
          <Document
            file={pdfUrl}
            onLoadSuccess={onDocumentLoadSuccess}
            loading={
              <div className="flex items-center justify-center py-20">
                <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
              </div>
            }
            error={
              <div className="text-red-600 text-sm">Failed to load PDF</div>
            }
          >
            <div className="flex flex-col items-center gap-4">
              {Array.from({ length: numPages }, (_, index) => (
                <div
                  key={`page_${index + 1}`}
                  data-testid={`pdf-page-wrapper-${index + 1}`}
                  ref={(el) => {
                    if (el) {
                      pageRefs.current.set(index + 1, el);
                    } else {
                      pageRefs.current.delete(index + 1);
                    }
                  }}
                >
                  <Page
                    pageNumber={index + 1}
                    width={pageWidth}
                    className="shadow-lg"
                    renderTextLayer={true}
                    renderAnnotationLayer={true}
                    onRenderSuccess={() => handlePageRenderSuccess(index + 1)}
                  />
                </div>
              ))}
            </div>
          </Document>
        ) : null}

        {showLoadingOverlay && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/70 backdrop-blur-[1px]">
            <Loader2 className="mb-2 h-8 w-8 animate-spin text-blue-600" />
            <p className="text-sm text-gray-600">{loadingMessage}</p>
          </div>
        )}
      </div>
    </div>
  );
}
